import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Flex, Text, Tooltip } from "@mantine/core";
import type { DeepKeys } from "@tanstack/table-core";
import identity from "lodash/identity";
import { type ReactNode, useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { PostPublishQuestionEditWarnings } from "../post-publish-edit-warnings";
import { QuestionWarningAlert } from "../question-warning-alert";
import { CANCEL_CONFIRMATION_PROPS } from "./assessment-question-field";
import { ConfirmationPopover } from "@/components/ui/confirmation-popover";
import {
  AssessmentQuestionFields,
  type CompetencyModalLoadingFields,
  type QuestionWarning,
  type SaveCompetencyPayload,
  SecondaryCompetencyModalLoadingFields,
} from "@/types/screener-builder";
import type { Nullable } from "@/types/utils";
import { POST_PUBLISH_EDIT_WARNINGS } from "@/utils/questions";

interface CompetencyModalButtonsProps {
  cancelConfirmationOpen: boolean;
  closeIconCloseConfirmation: () => void;
  closeCancelConfirmation: () => void;
  competency: SaveCompetencyPayload;
  getPrimaryButtonWrapper?: (children: ReactNode) => ReactNode;
  hasUnsavedChanges: boolean;
  isExistingCompetency: boolean;
  isPublishedScreener: boolean;
  isStepOne: boolean;
  llmWarning: Nullable<QuestionWarning>;
  loadingField: Nullable<CompetencyModalLoadingFields>;
  onBackClick: () => void;
  onCancelClick: () => void;
  onDangerButtonClick: () => void;
  onPrimaryButtonClick: () => void;
  openCancelConfirmation: () => void;
  primaryButtonDisabled: boolean;
  stepHasProgrammaticErrors: boolean;
}

const disabledPrimaryButtonStyles = twMerge([
  "disabled:bg-uw-purple",
  "disabled:opacity-50",
  "disabled:text-white",
]);

const STEP_ONE_EDIT_WARNINGS = new Map<DeepKeys<SaveCompetencyPayload>, string>(
  [
    ["competencyName", POST_PUBLISH_EDIT_WARNINGS.answerMismatch],
    ["displayName", POST_PUBLISH_EDIT_WARNINGS.answerMismatch],
    ["question", POST_PUBLISH_EDIT_WARNINGS.answerMismatch],
    ["competencyDefinition", POST_PUBLISH_EDIT_WARNINGS.definitionRescoring],
  ],
);
const STEP_TWO_EDIT_WARNINGS = new Map<DeepKeys<SaveCompetencyPayload>, string>(
  [
    ["highScoringAnswerDescription", POST_PUBLISH_EDIT_WARNINGS.rubric],
    ["lowScoringAnswerDescription", POST_PUBLISH_EDIT_WARNINGS.rubric],
    ["mediumScoringAnswerDescription", POST_PUBLISH_EDIT_WARNINGS.rubric],
    ["isCustomScoringRubric", POST_PUBLISH_EDIT_WARNINGS.rubric],
  ],
);

export const CompetencyModalButtons = ({
  cancelConfirmationOpen,
  closeIconCloseConfirmation,
  closeCancelConfirmation,
  competency,
  getPrimaryButtonWrapper = identity,
  hasUnsavedChanges,
  isExistingCompetency,
  isPublishedScreener,
  isStepOne,
  llmWarning,
  loadingField,
  onBackClick,
  onCancelClick,
  onDangerButtonClick,
  onPrimaryButtonClick,
  openCancelConfirmation,
  primaryButtonDisabled,
  stepHasProgrammaticErrors,
}: CompetencyModalButtonsProps) => {
  const loading = useMemo(() => !!loadingField, [loadingField]);

  const wrappedCancelHandler = useCallback(() => {
    if (hasUnsavedChanges) {
      closeIconCloseConfirmation();
      openCancelConfirmation();
    } else {
      onCancelClick();
    }
  }, [
    closeIconCloseConfirmation,
    hasUnsavedChanges,
    onCancelClick,
    openCancelConfirmation,
  ]);

  const primaryButtonText = useMemo(() => {
    if (!loadingField && llmWarning) {
      return "Check again";
    }

    switch (loadingField) {
      case AssessmentQuestionFields.Competency:
        return "Generating competency...";
      case AssessmentQuestionFields.Definition:
        return "Generating definition...";
      case AssessmentQuestionFields.Question:
        return "Generating questions...";
      case SecondaryCompetencyModalLoadingFields.Rubric:
        return "Generating rubric...";
      case SecondaryCompetencyModalLoadingFields.Validation:
        return "Checking your work...";
      case null:
      default:
        return isStepOne ? "Next" : "Save changes";
    }
  }, [isStepOne, llmWarning, loadingField]);

  const getTooltipProps = useCallback(
    (isDangerButton = false) => {
      const disabled = loading || !primaryButtonDisabled;

      if (stepHasProgrammaticErrors) {
        if (llmWarning) {
          return {
            disabled,
            label: `Fix the field-level errors highlighted above to ${isDangerButton ? "continue" : "check again"}`,
          };
        }

        return {
          disabled,
          label: "Fix errors to continue",
        };
      }
      return {
        disabled,
        label: "Complete all required fields",
      };
    },
    [llmWarning, loading, primaryButtonDisabled, stepHasProgrammaticErrors],
  );
  const primaryButton = useMemo(
    () => (
      <Tooltip {...getTooltipProps()}>
        {getPrimaryButtonWrapper(
          <Button
            className={disabledPrimaryButtonStyles}
            disabled={primaryButtonDisabled}
            onClick={onPrimaryButtonClick}
            rightSection={
              !loadingField &&
              !llmWarning &&
              isStepOne && <FontAwesomeIcon icon={faArrowRight} />
            }
          >
            {primaryButtonText}
          </Button>,
        )}
      </Tooltip>
    ),
    [
      getPrimaryButtonWrapper,
      getTooltipProps,
      isStepOne,
      llmWarning,
      loadingField,
      onPrimaryButtonClick,
      primaryButtonDisabled,
      primaryButtonText,
    ],
  );

  const validationWarning = useMemo(() => {
    if (llmWarning) {
      return (
        <QuestionWarningAlert warning={llmWarning}>
          <Text size="sm">{llmWarning.message}</Text>
          <Flex className="mt-4" gap={9}>
            {primaryButton}
            <Tooltip {...getTooltipProps(true)}>
              <Button
                className={twMerge([
                  "bg-uw-red disabled:opacity-50 text-white",
                ])}
                disabled={primaryButtonDisabled}
                onClick={onDangerButtonClick}
              >
                Override & continue
              </Button>
            </Tooltip>
          </Flex>
        </QuestionWarningAlert>
      );
    }
  }, [
    getTooltipProps,
    llmWarning,
    onDangerButtonClick,
    primaryButton,
    primaryButtonDisabled,
  ]);

  // For validation warnings on step one, we replace all button UI with the
  // validation warning button UI
  if (isStepOne && llmWarning) {
    return validationWarning;
  }

  return (
    <Flex direction="column" gap={18}>
      {/*
       *** For step 2, we leave the Back and Cancel buttons in place, rendering
       *** the validation warnings/buttons combo above them
       */}
      {validationWarning}
      <PostPublishQuestionEditWarnings
        enabled={isExistingCompetency && isPublishedScreener}
        value={competency}
        warningMap={isStepOne ? STEP_ONE_EDIT_WARNINGS : STEP_TWO_EDIT_WARNINGS}
      />
      <Flex direction="row" justify="space-between">
        {isStepOne ? (
          primaryButton
        ) : (
          <Flex gap={12}>
            <Button
              className={twMerge([
                "disabled:bg-transparent",
                "disabled:border-uw-purple",
                "disabled:opacity-50",
                "disabled:text-uw-purple",
              ])}
              disabled={loading}
              leftSection={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={onBackClick}
              variant="outline"
            >
              Back
            </Button>
            {/*
             *** On step 2 validation warning primary button functionality is
             *** replaced by the buttons in `validationWarning`
             */}
            {!llmWarning && primaryButton}
          </Flex>
        )}
        <ConfirmationPopover
          {...CANCEL_CONFIRMATION_PROPS}
          isOpen={cancelConfirmationOpen}
          onClose={closeCancelConfirmation}
          onPrimaryButtonClick={onCancelClick}
        >
          <Button c="gray.9" color="gray.0" onClick={wrappedCancelHandler}>
            Cancel
          </Button>
        </ConfirmationPopover>
      </Flex>
    </Flex>
  );
};
